import React from 'react';
import { useDrag, useDrop } from 'react-dnd';

/**
 * Reusable Item Card Component with PAR/MOQ Entry and Drag & Drop
 * 
 * Features:
 * - Drag and drop functionality
 * - PAR (Par Level) input with color coding
 * - MOQ (Minimum Order Quantity) input with color coding
 * - Position numbering
 * - Item information display (name, location, vendor)
 * - Delete functionality
 * - Click handler for item selection
 * - Responsive layout with fixed positioning
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - Item data object
 * @param {number} props.index - Item index for positioning
 * @param {string} props.location - Item location/group
 * @param {Function} props.onMove - Drag and drop move handler
 * @param {Function} props.onRemove - Remove item handler
 * @param {Function} props.onUpdatePar - PAR level update handler
 * @param {Function} props.onUpdateMOQ - MOQ update handler
 * @param {Function} props.onClick - Item click handler (optional)
 * @param {Object} props.config - Configuration options
 * @param {boolean} props.config.showPAR - Show PAR input (default: true)
 * @param {boolean} props.config.showMOQ - Show MOQ input (default: true)
 * @param {boolean} props.config.showDelete - Show delete button (default: true)
 * @param {boolean} props.config.showPosition - Show position number (default: true)
 * @param {boolean} props.config.enableDrag - Enable drag and drop (default: true)
 * @param {string} props.config.dragType - Drag type identifier (default: 'item-card')
 * @param {Object} props.styling - Custom styling options
 */
const ItemCard = ({
  item,
  index = 0,
  location = '',
  onMove,
  onRemove,
  onUpdatePar,
  onUpdateMOQ,
  onClick,
  config = {},
  styling = {},
  // Selection props can be passed at the top level (preferred — keeps them
  // referentially stable so React.memo can skip unrelated re-renders) or
  // nested inside `config` (legacy). Top-level values win when provided.
  selectable: selectableProp,
  selected: selectedProp,
  onToggleSelect: onToggleSelectProp
}) => {
  // Configuration with defaults
  const {
    showPAR = true,
    showMOQ = true,
    showDelete = true,
    showPosition = true,
    enableDrag = true,
    dragType = 'item-card',
    iconDelete = false,
    hideLocationVendorRows = false,
    selectable: selectableConfig = false,
    selected: selectedConfig = false,
    onToggleSelect: onToggleSelectConfig = null
  } = config;

  const selectable = selectableProp !== undefined ? selectableProp : selectableConfig;
  const selected = selectedProp !== undefined ? selectedProp : selectedConfig;
  const onToggleSelect =
    onToggleSelectProp !== undefined ? onToggleSelectProp : onToggleSelectConfig;

  const useFlexToolbar = hideLocationVendorRows;

  // Drag and drop setup (only if enabled)
  const [{ isDragging }, drag] = useDrag({
    type: dragType,
    item: { index, location, id: item.id },
    collect: m => ({ isDragging: m.isDragging() }),
    canDrag: enableDrag
  });

  const [, drop] = useDrop({
    accept: dragType,
    hover: d => {
      if (!enableDrag || !onMove) return;
      if (d.location !== location) return;
      if (d.index !== index) { 
        onMove(location, d.index, index); 
        d.index = index; 
      }
    }
  });

  // Extract item data with fallbacks
  const snap = item.item_snapshot || {};
  const unit = item.par_unit || snap.unit || 'each';
  const desc = snap.description || item.description || item.name || 'Unknown Item';
  const brand = snap.brand || item.brand;
  const vendor = snap.vendor || item.vendor;
  const category = snap.category || item.category;
  const loc = snap.location || item.inventoryLocation || item.location || location || 'Unassigned';
  const moqDisplayQty = item.min_order_qty ?? item.moq ?? 1;
  const moqDisplayUnit = item.min_order_unit || item.moq_unit || unit;

  // Handle item click
  const handleItemClick = (e) => {
    // Don't trigger click if clicking on inputs or buttons
    if (
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'BUTTON' ||
      e.target.closest('input, button') ||
      e.target.closest('.item-selection-checkbox')
    ) {
      return;
    }
    
    if (onClick) {
      onClick(item, index, location);
    }
    
    // Legacy support for global item editor
    if (window.updateItemEditor) {
      window.updateItemEditor(item);
    }
  };

  // Default styling with customization support
  const cardStyle = {
    minHeight: 64,
    cursor: enableDrag ? 'move' : 'default',
    ...styling.card
  };

  const positionStyle = {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#000000',
    opacity: '0.5',
    lineHeight: '1',
    ...styling.position
  };

  const dragHandleStyle = {
    color: 'rgb(36, 38, 41)',
    fontWeight: '100',
    fontSize: useFlexToolbar ? '0.85rem' : '1rem',
    ...styling.dragHandle
  };

  const leftInset = showPosition ? 35 : enableDrag ? 26 : selectable ? 30 : 8;

  const defaultInputPanelStyle = {
    position: 'absolute',
    left: '299px',
    right: '12px',
    top: '0',
    bottom: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: '4px',
    width: '75px',
    borderLeft: '2px solid var(--bs-border-color)',
    paddingLeft: '6px',
    paddingRight: '2px',
    backgroundColor: 'rgba(248, 249, 250, 0.5)',
    cursor: enableDrag ? 'move' : 'default'
  };

  const inputPanelStyle = {
    ...defaultInputPanelStyle,
    ...styling.inputPanel
  };

  return (
    <div
      ref={enableDrag ? n => drag(drop(n)) : undefined}
      className={`item-card-root position-relative ${useFlexToolbar ? 'mb-1' : 'mb-2'} bg-white border rounded ${isDragging ? 'opacity-50' : ''}`}
      style={{ ...cardStyle, padding: useFlexToolbar ? '0.25rem 0.35rem' : '0.375rem' }}
      onClick={handleItemClick}
    >
      {/* Position Number - Left Side */}
      {showPosition && (
        <div className="position-absolute" style={{ top: '50%', left: '8px', transform: 'translateY(-50%)', zIndex: 1 }}>
          <span style={positionStyle}>
            {(item.count_position || index + 1)}
          </span>
        </div>
      )}

      {/* Drag Handle - Left */}
      {enableDrag && (
        <div
          className="position-absolute text-muted"
          style={{
            top: '50%',
            left: selectable ? '24px' : '4px',
            transform: 'translateY(-50%)',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          <i className="feather icon-menu" style={dragHandleStyle}></i>
        </div>
      )}

      {selectable && (
        <div
          className="position-absolute item-selection-checkbox"
          style={{ top: '50%', left: '4px', transform: 'translateY(-50%)', zIndex: 15 }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            style={{ cursor: 'pointer' }}
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              if (typeof onToggleSelect === 'function') {
                onToggleSelect(item, e.target.checked);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${desc}`}
          />
        </div>
      )}

      <div
        className={useFlexToolbar ? 'd-flex align-items-center justify-content-between gap-1' : 'd-flex align-items-start justify-content-between'}
        style={{
          paddingLeft: `${leftInset}px`,
          paddingRight: useFlexToolbar ? '2px' : undefined,
          minWidth: 0
        }}
      >
        {/* Left Side - Item Info - Three Row Layout with Left Alignment */}
        <div
          className="flex-grow-1"
          style={{
            paddingRight: useFlexToolbar ? '4px' : (showPAR || showMOQ) ? '90px' : '20px',
            paddingTop: '1px',
            textAlign: 'left',
            minWidth: 0
          }}
        >
          {/* First Row - Item Name (Full Width) */}
          <div className={`fw-bold ${useFlexToolbar ? 'mb-0' : 'mb-1'}`} style={{
            fontSize: '0.75rem', /* Reduced from 0.8rem to match console changes */
            color: 'var(--bs-emphasis-color)',
            fontWeight: '600',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            hyphens: 'auto',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            fontFamily: 'var(--bs-body-font-family)',
            ...styling.itemName
          }}>
            {desc}
          </div>

          {useFlexToolbar && (loc || vendor) && (
            <div
              className="text-muted"
              style={{
                fontSize: '0.65rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={[loc, vendor].filter(Boolean).join(' · ')}
            >
              {[loc, vendor].filter(Boolean).join(' · ')}
            </div>
          )}

          {/* Second Row - Location with consistent tab stops */}
          {!hideLocationVendorRows && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--bs-secondary-color)',
            marginBottom: '2px',
            textAlign: 'left',
            ...styling.location
          }}>
            {loc && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}>
                <strong style={{
                  color: 'var(--bs-emphasis-color)',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                  width: '32px', /* Fixed tab stop width */
                  textAlign: 'left'
                }}>
                  Loc:
                </strong>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  textAlign: 'left'
                }} title={loc}>
                  {loc}
                </span>
              </div>
            )}
          </div>
          )}

          {/* Third Row - Vendor with consistent tab stops */}
          {!hideLocationVendorRows && (
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--bs-secondary-color)',
            textAlign: 'left',
            ...styling.vendor
          }}>
            {vendor && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start'
              }}>
                <strong style={{
                  color: 'var(--bs-emphasis-color)',
                  fontSize: '0.75rem',
                  flexShrink: 0,
                  width: '32px', /* Same tab stop width as Location */
                  textAlign: 'left'
                }}>
                  V:
                </strong>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  textAlign: 'left'
                }} title={vendor}>
                  {vendor}
                </span>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Right Side - PAR and MOQ Inputs - Fixed Position with Border */}
        {(showPAR || showMOQ) && (
          <div
            style={inputPanelStyle}
          >
            {/* PAR Input */}
            {showPAR && (
              <div className="d-flex align-items-center">
                <span className="me-1 fw-bold" style={{
                  fontSize: '0.7rem',
                  color: '#f0ad4e',
                  minWidth: '25px'
                }}>
                  PAR
                </span>
                <div className="position-relative">
                  <input
                    type="number"
                    className="form-control"
                    step="0.25"
                    min="0"
                    value={item.par_level ?? ''}
                    onChange={e => onUpdatePar && onUpdatePar(item.id, location, e.target.value, unit)}
                    style={{
                      width: '38px',
                      fontSize: '0.7rem',
                      padding: '2px 3px',
                      paddingRight: '12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #f0ad4e',
                      textAlign: 'left',
                      height: '24px',
                      ...styling.parInput
                    }}
                    placeholder="0"
                  />
                  <span
                    className="position-absolute text-muted"
                    style={{
                      right: '3px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '0.6rem',
                      pointerEvents: 'none',
                      color: '#6c757d'
                    }}
                  >
                    {(unit || 'each').substring(0, 2)}
                  </span>
                </div>
              </div>
            )}

            {/* MOQ Input */}
            {showMOQ && (
              <div className="d-flex align-items-center">
                <span className="me-1 fw-bold" style={{
                  fontSize: '0.7rem',
                  color: '#17a2b8',
                  minWidth: '25px'
                }}>
                  MOQ
                </span>
                <div className="position-relative">
                  <input
                    type="number"
                    className="form-control"
                    step="0.25"
                    min="0"
                    value={item.min_order_qty ?? ''}
                    onChange={e => onUpdateMOQ && onUpdateMOQ(item.id, e.target.value)}
                    style={{
                      width: '38px',
                      fontSize: '0.7rem',
                      padding: '2px 3px',
                      paddingRight: '12px',
                      backgroundColor: '#d1ecf1',
                      border: '1px solid #17a2b8',
                      textAlign: 'left',
                      height: '24px',
                      ...styling.moqInput
                    }}
                    placeholder="1"
                  />
                  <span
                    className="position-absolute text-muted"
                    style={{
                      right: '3px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '0.6rem',
                      pointerEvents: 'none',
                      color: '#6c757d'
                    }}
                  >
                    {(item.min_order_unit || unit || 'each').substring(0, 2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {useFlexToolbar && showDelete && onRemove && (
          <button
            type="button"
            className="btn btn-link text-danger p-0 flex-shrink-0"
            style={{ lineHeight: 1, minWidth: '1.5rem', ...styling.deleteButton }}
            title="Remove from group"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.id, location);
            }}
          >
            {iconDelete ? (
              <i className="feather icon-x" style={{ width: '16px', height: '16px' }} />
            ) : (
              '×'
            )}
          </button>
        )}
      </div>

      {/* Delete Button (legacy absolute layout) */}
      {!useFlexToolbar && showDelete && onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id, location);
          }}
          title="Remove"
          style={{
            position: 'absolute',
            left: '262px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#dc3545',
            cursor: 'pointer',
            zIndex: 1000,
            userSelect: 'none',
            ...styling.deleteButton
          }}
        >
          ×
        </span>
      )}
    </div>
  );
};

// Memoized: a single PAR/MOQ edit or selection toggle in a large template
// changes only one row's props, so untouched cards (whose item reference and
// scalar props are unchanged) skip re-rendering instead of all ~1000 redrawing.
export default React.memo(ItemCard);
